/**
 * Octave Design System - Theme Constants (Runtime CSS Variable Resolver)
 *
 * SINGLE SOURCE OF TRUTH: All design tokens are defined in
 * `src/styles/semantic-tokens.css`
 * under `:root { ... }`. This module is a thin JS/TS accessor that resolves
 * those CSS custom properties at runtime.
 *
 * Why: JS-side consumers (ECharts, MUI `sx`, inline styles) cannot use
 * `var(--...)` directly for color values that need to be parsed as strings.
 * Previously we duplicated the palette here as hex constants, which caused
 * silent drift when `semantic-tokens.css` changed. Now every export reads
 * from CSS at property access time, so `semantic-tokens.css` is the ONLY
 * place tokens are defined.
 *
 * Usage remains identical to before:
 *   import { CHART, BRAND, STATUS } from '@/config/theme';
 *   option.grid = CHART.grid;         // reads --chart-grid
 *   ctx.fillStyle = BRAND.black;      // reads --octave-black
 *
 * Notes:
 *  - Values are resolved to concrete RGB(A) strings (color-mix, keywords,
 *    and var() chains are all flattened by the browser), so ECharts /
 *    canvas / inline styles all receive plain color strings.
 *  - Results are cached; `setTheme()` clears the cache automatically.
 *  - Multi-theme ready: primitives live under `:root` (theme-invariant);
 *    semantic tokens live under `:root, [data-theme="dark"]`. To add a
 *    light theme, add `[data-theme="light"] { ... }` in
 *    `semantic-tokens.css` and call
 *    `setTheme('light')` — no component changes required.
 *  - If accessed before `document` exists (SSR / very early import),
 *    accessors return an empty string. In this SPA that never happens
 *    because `semantic-tokens.css` is imported before any React render.
 */

// ---------- CSS variable resolution ----------

const cache = new Map<string, string>();
let probeEl: HTMLDivElement | null = null;

const getProbe = (): HTMLDivElement | null => {
  if (typeof document === "undefined" || !document.body) return null;
  if (!probeEl) {
    probeEl = document.createElement("div");
    probeEl.style.display = "none";
    probeEl.setAttribute("aria-hidden", "true");
    document.body.appendChild(probeEl);
  }
  return probeEl;
};

const resolveVar = (varName: string): string => {
  const cached = cache.get(varName);
  if (cached !== undefined) return cached;

  const probe = getProbe();
  if (!probe) return "";

  probe.style.color = `var(${varName})`;
  const resolved = getComputedStyle(probe).color;

  if (resolved) cache.set(varName, resolved);
  return resolved || "";
};

const resolveRawVar = (varName: string): string => {
  if (typeof document === "undefined" || !document.documentElement) return "";
  return getComputedStyle(document.documentElement)
    .getPropertyValue(varName)
    .trim();
};

/** Clear the resolved-value cache (e.g. after a runtime theme switch). */
export const clearThemeCache = (): void => {
  cache.clear();
};

/**
 * Supported theme names. Extend this union when adding new themes
 * (e.g. `'high-contrast'`) — remember to add a matching
 * `[data-theme="..."]` block in `semantic-tokens.css`.
 */
export type ThemeName = "dark" | "light";

export const THEME_STORAGE_KEY = "cnc-theme";
export const DEFAULT_THEME: ThemeName = "light";

const isThemeName = (value: string | null): value is ThemeName =>
  value === "dark" || value === "light";

const getLocalStorage = (): Storage | null => {
  if (typeof window === "undefined") return null;
  try {
    return window.localStorage;
  } catch (error) {
    console.warn("Theme storage is unavailable:", error);
    return null;
  }
};

const readStoredTheme = (): ThemeName | null => {
  const storage = getLocalStorage();
  if (!storage) return null;

  try {
    const stored = storage.getItem(THEME_STORAGE_KEY);
    return isThemeName(stored) ? stored : null;
  } catch (error) {
    console.warn("Failed to read theme from storage:", error);
    return null;
  }
};

const persistTheme = (name: ThemeName): void => {
  const storage = getLocalStorage();
  if (!storage) return;

  try {
    storage.setItem(THEME_STORAGE_KEY, name);
  } catch (error) {
    console.warn("Failed to persist theme to storage:", error);
  }
};

/**
 * Apply a theme: sets `data-theme` on <html>, persists to localStorage,
 * and invalidates the CSS-variable cache.
 *
 * Called by ThemeContext (React) and can also be called from non-React code.
 */
export const setTheme = (name: ThemeName): void => {
  if (typeof document === "undefined" || !document.documentElement) return;
  document.documentElement.setAttribute("data-theme", name);
  persistTheme(name);
  clearThemeCache();
};

/** Read the currently active theme name from <html data-theme>. */
export const getTheme = (): ThemeName => {
  if (typeof document === "undefined" || !document.documentElement) return DEFAULT_THEME;
  const value = document.documentElement.getAttribute("data-theme");
  return isThemeName(value) ? value : DEFAULT_THEME;
};

/** Initialize theme from localStorage or fallback to current/default theme. */
export const initializeTheme = (): ThemeName => {
  if (typeof document === "undefined" || !document.documentElement) return DEFAULT_THEME;

  const stored = readStoredTheme();
  const themeName = stored ?? getTheme();
  setTheme(themeName);
  return themeName;
};

/**
 * Get any CSS variable value as a color string.
 * Exposed as an escape hatch for consumers that need ad-hoc access.
 */
export const getCSSVariable = (variableName: string): string =>
  resolveVar(variableName) || resolveRawVar(variableName);

type TokenMap = Readonly<Record<string, string>>;
type Resolved<T extends TokenMap> = { readonly [K in keyof T]: string };

const makeTokens = <T extends TokenMap>(map: T): Resolved<T> =>
  new Proxy({} as Resolved<T>, {
    get(_target, prop) {
      if (typeof prop !== "string") return undefined;
      const varName = map[prop];
      return varName ? resolveVar(varName) : undefined;
    },
    has: (_t, prop) => typeof prop === "string" && prop in map,
    ownKeys: () => Object.keys(map),
    getOwnPropertyDescriptor: (_t, prop) => {
      if (typeof prop === "string" && prop in map) {
        return { enumerable: true, configurable: true, writable: false };
      }
      return undefined;
    },
  });


const makePalette = (varNames: readonly string[]): readonly string[] => {
  const target: string[] = [];
  return new Proxy(target, {
    get(_t, prop) {
      if (prop === "length") return varNames.length;
      if (prop === Symbol.iterator) {
        return function* () {
          for (const v of varNames) yield resolveVar(v);
        };
      }
      if (typeof prop === "string" && /^\d+$/.test(prop)) {
        const idx = Number(prop);
        return idx < varNames.length ? resolveVar(varNames[idx]) : undefined;
      }
      const snapshot = varNames.map(resolveVar);
      const value = (snapshot as unknown as Record<string | symbol, unknown>)[prop];
      return typeof value === "function" ? value.bind(snapshot) : value;
    },
  }) as readonly string[];
};

// ========== PRIMARY BRAND COLORS ==========
export const BRAND = makeTokens({
  black: "--octave-black",
  gray5: "--gray-5",
  gray4: "--gray-4",
  gray3: "--gray-3",
  gray2: "--gray-2",
  gray1: "--gray-1",
  white: "--octave-white",
});

// ========== ACCENT COLORS ==========
export const ACCENT = makeTokens({
  blue: "--accent-blue",
  aqua: "--accent-aqua",
  chartBlue: "--accent-chart-blue",
  comparisonCurrent: "--text-current-value-color",
  green: "--accent-green",
  yellow: "--accent-yellow",
  orange: "--accent-orange",
  pink: "--accent-pink",
  purple: "--accent-purple",
});

// ========== BACKGROUND COLORS ==========
export const BG = makeTokens({
  canvas: "--bg-canvas",
  surface: "--bg-surface",
  elevated: "--bg-elevated",
  sidebar: "--bg-sidebar",
  secondaryNav: "--bg-secondary-nav",
  hover: "--bg-hover",
  hoverSubtle: "--bg-hover-subtle",
  overlay: "--bg-overlay",
  accentSubtle: "--bg-accent-subtle",
  aquaSubtle: "--bg-accent-subtle",
});

// ========== TEXT COLORS ==========
export const TEXT = makeTokens({
  primary: "--text-primary",
  secondary: "--text-secondary",
  tertiary: "--text-tertiary",
  disabled: "--text-disabled",
  inverse: "--text-black",
});

// ========== ICON COLORS (per Octave: black or white only) ==========
export const ICON = makeTokens({
  onDark: "--icon-on-dark",
  onDarkMuted: "--gray-3",
  onLight: "--icon-on-light",
});

// ========== BORDER COLORS ==========
export const BORDER = makeTokens({
  default: "--border-default",
  subtle: "--border-subtle",
  subtleLight: "--border-subtle-light",
  divider: "--divider",
});

// ========== STATUS COLORS ==========
export const STATUS = makeTokens({
  success: "--status-success",
  warning: "--status-warning",
  running: "--status-running",
  error: "--status-error",
  info: "--status-info",
});

// ========== CHART COLORS ==========
// NOTE: Composite CSS values (e.g. --shadow-chart-tooltip / --input-focus-shadow)
// contain box-shadow definitions, not plain colors. We expose them via getCSSVariable
// so ECharts / inline styles get the raw declared string.
export const CHART: Readonly<{
  bg: string;
  grid: string;
  axis: string;
  axisLabel: string;
  tooltipBg: string;
  tooltipBorder: string;
  tooltipText: string;
  tooltipShadow: string;
  linePrimary: string;
  lineHover: string;
  areaFill: string;
  areaGradientStart: string;
  areaGradientEnd: string;
  weeklyAreaGradientStart: string;
  weeklyAreaGradientEnd: string;
  glow: string;
  emphasisShadow: string;
}> = new Proxy({} as never, {
  get(_t, prop) {
    switch (prop) {
      case "bg": return resolveRawVar("--chart-bg") || "transparent";
      case "grid": return resolveVar("--chart-grid");
      case "axis": return resolveVar("--chart-axis");
      case "axisLabel": return resolveVar("--chart-axis-label");
      case "tooltipBg": return resolveVar("--chart-tooltip-bg");
      case "tooltipBorder": return resolveVar("--chart-tooltip-border");
      case "tooltipText": return resolveVar("--chart-tooltip-text");
      case "tooltipShadow": return resolveRawVar("--shadow-chart-tooltip");
      case "linePrimary": return resolveVar("--chart-line-primary");
      case "lineHover": return resolveVar("--chart-line-primary");
      case "areaFill": return resolveVar("--chart-area-fill");
      case "areaGradientStart": return resolveVar("--chart-area-gradient-start");
      case "areaGradientEnd": return resolveVar("--chart-area-gradient-end");
      case "weeklyAreaGradientStart": return resolveVar("--weekly-chart-area-gradient-start");
      case "weeklyAreaGradientEnd": return resolveVar("--weekly-chart-area-gradient-end");
      case "glow": return resolveVar("--chart-glow");
      case "emphasisShadow": return resolveVar("--chart-emphasis-shadow");
      default: return undefined;
    }
  },
});

// ========== DROPDOWN COLORS ==========
export const DROPDOWN = makeTokens({
  bg: "--dropdown-bg",
  border: "--dropdown-border",
  itemText: "--dropdown-item-text",
  itemHoverBg: "--dropdown-item-hover-bg",
  itemHoverText: "--dropdown-item-hover-text",
  itemSelectedBg: "--dropdown-item-selected-bg",
  itemSelectedText: "--dropdown-item-selected-text",
  arrow: "--dropdown-arrow",
});

// ========== INPUT COLORS ==========
// focusShadow is a full box-shadow declaration (composite value), not a color.
export const INPUT: Readonly<{
  bg: string; border: string; text: string; placeholder: string;
  focusBorder: string; focusShadow: string;
  hoverBg: string; hoverBorder: string;
  disabledBg: string; disabledBorder: string; disabledText: string;
}> = new Proxy({} as never, {
  get(_t, prop) {
    switch (prop) {
      case "bg": return resolveVar("--input-bg");
      case "border": return resolveVar("--input-border");
      case "text": return resolveVar("--input-text");
      case "placeholder": return resolveVar("--input-placeholder");
      case "focusBorder": return resolveVar("--input-focus-border");
      case "focusShadow": return resolveRawVar("--input-focus-shadow");
      case "hoverBg": return resolveVar("--input-hover-bg");
      case "hoverBorder": return resolveVar("--input-hover-border");
      case "disabledBg": return resolveVar("--input-disabled-bg");
      case "disabledBorder": return resolveVar("--input-disabled-border");
      case "disabledText": return resolveVar("--input-disabled-text");
      default: return undefined;
    }
  },
});

// ========== NAVIGATION COLORS ==========
export const NAV = makeTokens({
  sidebarBg: "--bg-sidebar",
  headerBg: "--bg-sidebar",
  secondaryBg: "--nav-secondary-bg",
  textDefault: "--nav-text-default",
  textActive: "--nav-text-active",
  activeIndicator: "--nav-active-indicator",
  activeIndicatorAqua: "--nav-active-indicator",
  hoverBg: "--nav-hover-bg",
  hoverBgSubtle: "--nav-hover-bg",
  activeBg: "--nav-active-bg",
  headerBorder: "--nav-header-border",
  secondaryBorder: "--nav-secondary-border",
});

// ========== TABLE COLORS ==========
export const TABLE = makeTokens({
  headerBg: "--table-header-bg",
  headerText: "--table-header-text",
  rowBg: "--table-row-bg",
  rowHover: "--table-row-hover",
  cellText: "--table-cell-text",
  border: "--table-border",
});

// ========== COMPARISON CHART COLORS ==========
export const COMPARISON = makeTokens({
  previous: "--comparison-previous",
  current: "--comparison-current",
  noChange: "--comparison-no-change",
  increase: "--comparison-increase",
  decrease: "--comparison-decrease",
});

// ========== KPI CARD COLORS ==========
// valueSize is a length, not a color — pass through raw.
export const KPI: Readonly<{
  bg: string; border: string; title: string; value: string;
  valueSize: string; trendPositive: string; trendNegative: string;
}> = new Proxy({} as never, {
  get(_t, prop) {
    switch (prop) {
      case "bg": return resolveVar("--kpi-bg");
      case "border": return resolveVar("--kpi-border");
      case "title": return resolveVar("--kpi-title");
      case "value": return resolveVar("--kpi-value");
      case "valueSize": return resolveRawVar("--kpi-value-size");
      case "trendPositive": return resolveVar("--kpi-trend-positive");
      case "trendNegative": return resolveVar("--kpi-trend-negative");
      default: return undefined;
    }
  },
});

// ========== WIDGET COLORS ==========
export const WIDGET = makeTokens({
  bg: "--card-bg",
  border: "--card-border",
  headerBg: "--widget-header-bg",
  headerBorder: "--widget-header-border",
  title: "--widget-title",
  icon: "--widget-icon",
  iconHover: "--octave-white",
});

// ========== GEO MAP COLORS ==========
export const GEO_MAP = {
  light: makeTokens({
    bg: "--geo-map-light-bg",
    land: "--geo-map-light-land",
    landHover: "--geo-map-light-land-hover",
    border: "--geo-map-light-border",
    text: "--geo-map-light-text",
    emphasisBorder: "--geo-map-light-emphasis-border",
    emphasisShadow: "--geo-map-light-emphasis-shadow",
    scatterBorder: "--geo-map-light-scatter-border",
  }),
  dark: makeTokens({
    bg: "--geo-map-dark-bg",
    land: "--geo-map-dark-land",
    landHover: "--geo-map-dark-land-hover",
    border: "--geo-map-dark-border",
    text: "--geo-map-dark-text",
    emphasisBorder: "--geo-map-dark-emphasis-border",
    emphasisShadow: "--geo-map-dark-emphasis-shadow",
    scatterBorder: "--geo-map-dark-scatter-border",
  }),
} as const;

// ========== CHART DATA PALETTE ==========
export const CHART_PALETTE = makePalette([
  "--chart-palette-1",
  "--chart-palette-2",
  "--chart-palette-3",
  "--chart-palette-4",
  "--chart-palette-5",
  "--chart-palette-6",
  "--chart-palette-7",
  "--chart-palette-8",
  "--chart-palette-9",
  "--chart-palette-10",
  "--chart-palette-11",
  "--chart-palette-12",
]);

// ========== TABLE HEADER (THEAD) COLORS ==========
// fontWeight / fontSize are pass-through typography tokens.
export const THEAD: Readonly<{ color: string; fontWeight: string; fontSize: string }> =
  new Proxy({} as never, {
    get(_t, prop) {
      switch (prop) {
        case "color": return resolveVar("--status-info");
        case "fontWeight": return resolveRawVar("--font-weight-semibold") || "600";
        case "fontSize": return resolveRawVar("--font-caption") || "12px";
        default: return undefined;
      }
    },
  });

// ========== DEFAULT EXPORT ==========
const theme = {
  brand: BRAND,
  accent: ACCENT,
  bg: BG,
  text: TEXT,
  icon: ICON,
  border: BORDER,
  status: STATUS,
  chart: CHART,
  dropdown: DROPDOWN,
  input: INPUT,
  nav: NAV,
  table: TABLE,
  kpi: KPI,
  widget: WIDGET,
  comparison: COMPARISON,
  chartPalette: CHART_PALETTE,
  thead: THEAD,
  geoMap: GEO_MAP,
  getCSSVariable,
  clearThemeCache,
  setTheme,
  getTheme,
  initializeTheme,
} as const;

export default theme;
